//
// Standard Percentage (0 - 100%)

import type { z } from "zod";
import type { FormConfig, I18NFunction, ObjectConfig } from "../form-config-types";
import { initialPresentationForm } from "./01-initial-presentation-form-data";
import { confirmatoryDiagnosisForm } from "./02-confirmatory-diagnosis";
import { treatmentEarlyStageForm } from "./03-treatment-early-stage";
import { treatmentLateStageForm } from "./04-treatment-late-stage";
import { ongoingManagementForm } from "./05-ongoing-management";

/**
 * find any dependencies, then return the path
 * @param config
 * @returns
 */
export function getFieldsWithDependencies({
  config,
  parentObject,
}: {
  config: FormConfig;
  parentObject?: {
    id: string;
    config: ObjectConfig;
  };
}) {
  const fieldsWithDependencies: string[] = [];
  // we know we are in an object if the keyPrefix is not an empty string
  // const isInObject = keyPrefix !== "";

  Object.entries(config).forEach(([key, value]) => {
    //
    // forceWatch
    //
    if (parentObject?.config?.offerNotApplicableForEntireObject) {
      fieldsWithDependencies.push(`${parentObject.id}.${key}`);
      return;
    }

    if (value.dependencies) {
      value.dependencies.forEach((dependency) => {
        if (dependency.if) {
          fieldsWithDependencies.push(dependency.if[0]);
        }
        if (dependency.or) {
          fieldsWithDependencies.push(dependency.or[0]);
        }
        if (dependency.or2) {
          fieldsWithDependencies.push(dependency.or2[0]);
        }
        if (dependency.and) {
          fieldsWithDependencies.push(dependency.and[0]);
        }
        if (dependency.and2) {
          fieldsWithDependencies.push(dependency.and2[0]);
        }
      });
      return;
    }

    //
    // hideIf
    //
    // if (value.hideIf) {
    //   fieldsWithDependencies.push(value.hideIf[0]);
    //   return;
    // }

    //
    // requireIf
    //
    // if (value.requireIf) {
    //   watchedFields.push(value.requireIf[0]);
    // }

    if (value.type === "object") {
      // if the object has offerNotApplicableForEntireObject, we need to watch all it's properties
      fieldsWithDependencies.push(
        ...getFieldsWithDependencies({ config: value.properties, parentObject: { id: key, config: value } })
      );
    }
  });

  // remove duplicates
  return [...new Set(fieldsWithDependencies)];
}

export const getForms = (l: I18NFunction) => {
  return [
    initialPresentationForm(l),
    confirmatoryDiagnosisForm(l),
    treatmentEarlyStageForm(l),
    treatmentLateStageForm(l),
    ongoingManagementForm(l),
  ];
};

export const enumErrorMap: (l: I18NFunction) => z.ZodErrorMap = (l) => (issue, ctx) => {
  // Handle different error types
  if (issue.code === "invalid_type") {
    return {
      message: l("Please select an option", "Veuillez sélectionner une option"),
    };
  }
  if (issue.code === "invalid_enum_value") {
    return {
      message: l("Please select a valid option", "Veuillez sélectionner une option valide"),
    };
  }
  // Default
  return {
    message: l("Please select an option", "Veuillez sélectionner une option"),
  };
};

export const arrayErrorMap: (l: I18NFunction) => z.ZodErrorMap = (l) => (issue, ctx) => {
  // Handle "Expected array, received null" type errors
  if (issue.code === "invalid_type") {
    if (issue.expected === "array") {
      return {
        message: l("Please select at least one option", "Veuillez sélectionner au moins une option"),
      };
    }
    return {
      message: l("Invalid value", "Valeur invalide"),
    };
  }

  // Handle array.min() errors
  if (issue.code === "too_small") {
    if (issue.minimum === 1) {
      return {
        message: l("Please select at least one option", "Veuillez sélectionner au moins une option"),
      };
    }
    return {
      message: l(
        `Please select at least ${issue.minimum} options`,
        `Veuillez sélectionner au moins ${issue.minimum} options`
      ),
    };
  }

  // Default
  return {
    message: l("Please select at least one option", "Veuillez sélectionner au moins une option"),
  };
};

/**
 * Comprehensive error map that handles all common Zod validation errors
 * with user-friendly bilingual messages
 */
export const comprehensiveErrorMap: (l: I18NFunction) => z.ZodErrorMap = (l) => (issue, ctx) => {
  // If a custom message is already provided, use it
  if (ctx.defaultError !== "Invalid input") {
    return { message: ctx.defaultError };
  }

  switch (issue.code) {
    case "invalid_type":
      if (issue.received === "null" || issue.received === "undefined") {
        // Handle null/undefined for different expected types
        if (issue.expected === "string") {
          return { message: l("This field is required", "Ce champ est requis") };
        }
        if (issue.expected === "array") {
          return { message: l("Please select at least one option", "Veuillez sélectionner au moins une option") };
        }
        if (issue.expected === "number") {
          return { message: l("Please enter a number", "Veuillez entrer un nombre") };
        }
        if (issue.expected === "boolean") {
          return { message: l("Please select an option", "Veuillez sélectionner une option") };
        }
        return { message: l("This field is required", "Ce champ est requis") };
      }
      return { message: l("Invalid value type", "Type de valeur invalide") };

    case "invalid_enum_value":
      return { message: l("Please select a valid option", "Veuillez sélectionner une option valide") };

    case "too_small":
      if (issue.type === "string") {
        if (issue.minimum === 1) {
          return { message: l("This field is required", "Ce champ est requis") };
        }
        return {
          message: l(
            `Please enter at least ${issue.minimum} characters`,
            `Veuillez entrer au moins ${issue.minimum} caractères`
          ),
        };
      }
      if (issue.type === "array") {
        if (issue.minimum === 1) {
          return { message: l("Please select at least one option", "Veuillez sélectionner au moins une option") };
        }
        return {
          message: l(
            `Please select at least ${issue.minimum} options`,
            `Veuillez sélectionner au moins ${issue.minimum} options`
          ),
        };
      }
      if (issue.type === "number") {
        return {
          message: l(
            `Value must be at least ${issue.minimum}`,
            `La valeur doit être d'au moins ${issue.minimum}`
          ),
        };
      }
      return { message: l("Value is too small", "Valeur trop petite") };

    case "too_big":
      if (issue.type === "string") {
        return {
          message: l(
            `Please enter no more than ${issue.maximum} characters`,
            `Veuillez entrer au maximum ${issue.maximum} caractères`
          ),
        };
      }
      if (issue.type === "array") {
        return {
          message: l(
            `Please select no more than ${issue.maximum} options`,
            `Veuillez sélectionner au maximum ${issue.maximum} options`
          ),
        };
      }
      if (issue.type === "number") {
        return {
          message: l(`Value must be at most ${issue.maximum}`, `La valeur doit être au maximum ${issue.maximum}`),
        };
      }
      return { message: l("Value is too large", "Valeur trop grande") };

    case "invalid_string":
      if (issue.validation === "email") {
        return { message: l("Please enter a valid email address", "Veuillez entrer une adresse e-mail valide") };
      }
      if (issue.validation === "url") {
        return { message: l("Please enter a valid URL", "Veuillez entrer une URL valide") };
      }
      return { message: l("Invalid format", "Format invalide") };

    case "invalid_literal":
      return { message: l("Invalid value", "Valeur invalide") };

    case "unrecognized_keys":
      return { message: l("Unrecognized field(s)", "Champ(s) non reconnu(s)") };

    case "invalid_union":
      return { message: l("Invalid value", "Valeur invalide") };

    case "invalid_date":
      return { message: l("Please enter a valid date", "Veuillez entrer une date valide") };

    case "custom":
      // Custom errors should already have messages
      return { message: issue.message ?? l("Invalid value", "Valeur invalide") };

    default:
      // Fallback for any unhandled error codes
      return { message: l("Invalid value", "Valeur invalide") };
  }
};
